import { useEffect, useState } from "preact/hooks";
import { getVoices, setVoiceEnabled, type VoiceList, type VoiceOption } from "./api.ts";
import { message } from "./errors.ts";
import { Close, Play } from "./icons.tsx";
import { Button, Callout, Card, Pill, Row, ScreenHead, ScreenTitle } from "./ui.tsx";
import { useAction } from "./use-action.ts";
import { loadVoiceSamples, voiceSampleUrl } from "./voice-samples.ts";
import { isVoiceEnabled, voiceInitial, voiceLabel } from "./voices.ts";

interface Props {
  onBack: () => void;
}

/**
 * Which voices the message creator offers. Open to every staffer, the same as
 * topics and digits: it shapes content rather than access.
 */
export function VoiceCatalog({ onBack }: Props) {
  const [list, setList] = useState<VoiceList | null>(null);
  const [samples, setSamples] = useState<ReadonlySet<string>>(new Set());
  const [playing, setPlaying] = useState<{ what: string; url: string } | null>(null);
  const { busy, error, setError, run } = useAction();

  useEffect(() => {
    getVoices().then(setList, (reason: unknown) => setError(message(reason)));
    loadVoiceSamples().then(setSamples, () => undefined);
  }, [setError]);

  function toggle(voice: VoiceOption, enabled: boolean): void {
    void run(async () => {
      const next = await setVoiceEnabled(voice.id, enabled);
      // The API answers with the whole disabled set, so nothing is re-fetched.
      setList((current) =>
        current ? { ...current, disabled: next.disabled } : current,
      );
    });
  }

  return (
    <>
      <ScreenHead onBack={onBack} disabled={busy} />
      <ScreenTitle>Voices</ScreenTitle>
      <p class="lede">
        Turn off the voices you don't want to see when writing a message. Any message
        that already uses a voice keeps it and keeps playing.
      </p>

      {playing && (
        <div class="player">
          <span class="player-what">Playing {playing.what}</span>
          {/* biome-ignore lint/a11y/useMediaCaption: every sample says the same sentence */}
          <audio controls autoPlay src={playing.url} />
          <button
            type="button"
            class="icon-btn"
            aria-label="Stop playing"
            onClick={() => setPlaying(null)}
          >
            <Close />
          </button>
        </div>
      )}

      {error && <Callout tone="bad" title={error} onDismiss={() => setError(null)} />}
      {!list && !error && <p class="empty">Loading…</p>}

      {list && (
        <Card class="card-rows">
          {list.voices.map((voice) => {
            const enabled = isVoiceEnabled(list, voice.id);
            return (
              <Row
                key={voice.id}
                class="row-voice"
                leading={
                  <span class="voice-face" aria-hidden="true">
                    {voiceInitial(voice.name)}
                  </span>
                }
                title={voiceLabel(voice.name)}
                meta={
                  <>
                    <Pill tone={enabled ? "enabled" : "disabled"} />
                    {voice.name === list.defaultVoice && (
                      <Pill tone="brand" label="Default" />
                    )}
                  </>
                }
                body={<p class="voice-line">{voice.description}</p>}
                trailing={
                  <>
                    {samples.has(voice.id) && (
                      <Button
                        disabled={busy}
                        onClick={() => {
                          setPlaying({
                            what: voiceLabel(voice.name),
                            url: voiceSampleUrl(voice.id),
                          });
                        }}
                      >
                        <Play />
                        <span>Hear voice</span>
                      </Button>
                    )}
                    <Button
                      disabled={busy}
                      onClick={() => toggle(voice, !enabled)}
                      aria-label={`${enabled ? "Disable" : "Enable"} ${voiceLabel(voice.name)}`}
                    >
                      {enabled ? "Disable" : "Enable"}
                    </Button>
                  </>
                }
              />
            );
          })}
        </Card>
      )}
    </>
  );
}
